/**
 * PGSS-5472 — a stable React key for a react-hook-form field-array row.
 *
 * Quantities appeared to shift onto the wrong line after deleting a product on a NEW order or
 * quote: delete Product A and B showed A's quantity, C showed B's. The data was never wrong — the
 * Edit dialog, the save, and reopening the order all showed the right numbers. Only the rendered
 * inputs were stale.
 *
 * The cause was the row key. OrderItems rendered `key={data.id || index}`, where `data.id` is the
 * record's OWN id — which a row that has been added but not yet saved does not have. So on a new
 * order the key fell back to the array index, React reused the row components by position when an
 * earlier row was removed, and the inputs kept the value of the row that used to sit there. On a
 * saved order every row has an id, which is why it only ever happened before the first save.
 *
 * react-hook-form already generates a stable per-row key. It is just not called `id` here: both
 * field arrays feeding this screen set an explicit `keyName`, and neither value was being used.
 *
 *   ProductsTab    useFieldArray({ name: 'order_details', keyName: 'key' })      -> field.key
 *   ProductDialog  useFieldArray({ name: 'products',      keyName: 'field_id' }) -> field.field_id
 *
 * Both render the same OrderItems, so the key has to satisfy both. Hence the order below: prefer
 * whichever generated key this parent produced, and only then fall back.
 *
 * The `index` fallback is kept deliberately as a last resort — a key is required, and a missing
 * one is worse than an unstable one — but nothing that reaches this function through either
 * parent should ever need it.
 *
 * @param {object} field a row from useFieldArray's `fields`
 * @param {number} index its position, used only if the row carries no identity at all
 * @returns {string|number}
 */
export default function fieldRowKey(field, index) {
  if (!field) {
    return index;
  }
  if (field.key !== undefined && field.key !== null) {
    return field.key;
  }
  if (field.field_id !== undefined && field.field_id !== null) {
    return field.field_id;
  }
  if (field.id !== undefined && field.id !== null) {
    return field.id;
  }
  return index;
}
